import { useCallback, useState } from "react";

// Constants
const MAX_BRANCHES_PREVIEW = 3;
const MAX_REGIONS_PREVIEW = 3;
const MAX_ATMS_PREVIEW = 3;

// Component for rendering branch information using branchDetails from API
const BranchInfo = ({ branchDetails }) => {
  const [showFullList, setShowFullList] = useState(false);

  const toggleBranchList = useCallback(() => {
    setShowFullList((prev) => !prev);
  }, []);

  if (!branchDetails?.length) {
    return <span>No branches</span>;
  }

  const branchNames = branchDetails.map((branch) => branch.branchName);

  if (branchNames.length <= MAX_BRANCHES_PREVIEW) {
    return <span>{branchNames.join(", ")}</span>;
  }

  return (
    <span>
      {showFullList ? (
        <span className="branches-list">
          {branchNames.join(", ")}{" "}
          <span
            className="more-branches primary-color"
            onClick={toggleBranchList}
          >
            Show less
          </span>
        </span>
      ) : (
        <span>
          {branchNames.slice(0, MAX_BRANCHES_PREVIEW).join(", ")}
          <span
            title={branchNames.join(", ")}
            className="more-branches primary-color"
            onClick={toggleBranchList}
          >
            {" "}
            +{branchNames.length - MAX_BRANCHES_PREVIEW} more
          </span>
        </span>
      )}
    </span>
  );
};

// Tile component for individual metrics
const InfoTile = ({ label, value, className = "" }) => (
  <div className={`user-detail ${className}`}>
    <span className="detail-label color-light">{label}</span>
    <span className="detail-value color-dark">{value}</span>
  </div>
);

// Main TileInfo Component
const TileInfo = ({ userData }) => {
  const [showAllRegions, setShowAllRegions] = useState(false);
  const [showAllATMs, setShowAllATMs] = useState(false);

  if (!userData) return null;

  // Calculate regions and ATMs for restricted users
  const regions = userData?.branchDetails
    ? [...new Set(userData.branchDetails.map((branch) => branch.region))]
    : [];
  const allATMs = userData?.branchDetails
    ? userData.branchDetails.flatMap((branch) => branch.devices || [])
    : [];

  const hasMultipleRegions = regions.length > MAX_REGIONS_PREVIEW;
  const hasMultipleATMs = allATMs.length > MAX_ATMS_PREVIEW;

  // Render regions value
  const renderRegionsValue = () => {
    if (regions.length === 0) return "No regions";

    if (showAllRegions) {
      return (
        <>
          {regions.join(", ")}
          {hasMultipleRegions && (
            <span
              className="more-regions primary-color"
              onClick={() => setShowAllRegions(false)}
            >
              {" "}
              Show less
            </span>
          )}
        </>
      );
    } else {
      return (
        <>
          {regions.slice(0, MAX_REGIONS_PREVIEW).join(", ")}
          {hasMultipleRegions && (
            <span
              title={regions.join(", ")}
              className="more-regions primary-color"
              onClick={() => setShowAllRegions(true)}
            >
              {" "}
              +{regions.length - MAX_REGIONS_PREVIEW} more
            </span>
          )}
        </>
      );
    }
  };

  // Render ATMs value
  const renderATMsValue = () => {
    if (allATMs.length === 0) return "No ATMs";

    if (showAllATMs) {
      return (
        <>
          {allATMs.join(", ")}
          {hasMultipleATMs && (
            <span
              className="more-atms primary-color"
              onClick={() => setShowAllATMs(false)}
            >
              {" "}
              Show less
            </span>
          )}
        </>
      );
    } else {
      return (
        <>
          {allATMs.slice(0, MAX_ATMS_PREVIEW).join(", ")}
          {hasMultipleATMs && (
            <span
              title={allATMs.join(", ")}
              className="more-atms primary-color"
              onClick={() => setShowAllATMs(true)}
            >
              {" "}
              +{allATMs.length - MAX_ATMS_PREVIEW} more
            </span>
          )}
        </>
      );
    }
  };

  return (
    <>
      <div className={`user-details-grid`}>
        {/* Branch Tile - Always show if branchDetails exists */}
        {userData?.branchDetails !== null && (
          <InfoTile
            label="BRANCH(ES)"
            value={<BranchInfo branchDetails={userData?.branchDetails} />}
          />
        )}

        {/* Region Tile - Show for restricted users */}
        {userData?.restrictedUser && (
          <InfoTile label="REGION(S)" value={renderRegionsValue()} />
        )}

        {/* ATM Tile - Show for restricted users */}
        {userData?.restrictedUser && (
          <InfoTile label="ATM(S)" value={renderATMsValue()} />
        )}
      </div>
    </>
  );
};

export default TileInfo;
